import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardMedia,
  CardActionArea,
  Typography,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery,
  Tooltip,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Star, Checkroom, Tune, ViewCarousel, GridView, ShoppingBagOutlined } from '@mui/icons-material';
import { ImageMetadata, ProductImageGroup, ImageSrcSet, ProductActionSourceProduct } from '../types/conversation';
import { Citation } from '../types/citation';
import { useAppConfig } from '../contexts/ConfigContext';
import { useConversation } from '../contexts/ConversationContext';
import { extractProductUrlsFromStructured, enrichImagesWithProductData, isStructuredResponse, extractProductsFromPartialJSON, enrichImagesFromStreamingProducts, ProductReference } from '../utils/structuredResponseParser';

/**
 * Helper function to extract metadata fields from ImageMetadata.
 * Checks both top-level properties (from LLM structured output) and nested metadata object (from Azure Search).
 */
function extractMetadataField<T>(image: ImageMetadata, fieldName: string): T | undefined {
  // First check top-level property (from LLM structured output like product_desc, color, sizes)
  const topLevel = (image as any)[fieldName];
  if (topLevel !== undefined && topLevel !== null) {
    return topLevel;
  }
  
  // Then check nested metadata object (from Azure Search index fields)
  if (image.metadata && typeof image.metadata === 'object') {
    const nested = (image.metadata as any)[fieldName];
    if (nested !== undefined && nested !== null) {
      return nested;
    }
  }
  
  return undefined;
}

interface ImageCarouselProps {
  images: ImageMetadata[];
  citations?: Citation[];
  messageContent?: string; // NEW: Full message content for structured response parsing
  onImageClick?: (image: ImageMetadata) => void;
  onOutfitSuggestion?: (sourceProduct: ProductActionSourceProduct) => void;
}
interface ImageCardProps {
  image: ImageMetadata;
  onClick?: (image: ImageMetadata) => void;
  onOutfitSuggestion?: (sourceProduct: ProductActionSourceProduct) => void;
  loading?: boolean;
  config?: any;
}

interface ProductImageCardProps {
  productGroup: ProductImageGroup;
  onClick?: (image: ImageMetadata) => void;
  onOutfitSuggestion?: (sourceProduct: ProductActionSourceProduct) => void;
  loading?: boolean;
  config?: any;
}

// Helper function to build srcset string from ImageSrcSet array
const buildSrcSetString = (srcset?: ImageSrcSet[]): string | undefined => {
  if (!srcset || srcset.length === 0) return undefined;
  return srcset.map(src => `${src.url} ${src.width}w`).join(', ');
};

// Helper function to format sizes intelligently based on available space
// Shows as many sizes as can fit within maxChars, then adds "+N more" for remaining
const formatSizesForDisplay = (
  sizes: string[], 
  maxChars: number = 25,
  label: string = 'Sizes:',
  moreText: string = 'more'
): { display: string; hasMore: boolean; remaining: number } => {
  if (sizes.length === 0) return { display: '', hasMore: false, remaining: 0 };
  
  // Use configurable label prefix (add space if label is not empty)
  const prefix = label ? `${label} ` : '';
  let display = sizes[0];
  let count = 1;
  
  // Keep adding sizes while we have room (accounting for prefix and potential " +N more" suffix)
  while (count < sizes.length) {
    const nextSize = sizes[count];
    const testDisplay = `${display}, ${nextSize}`;
    const remaining = sizes.length - (count + 1);
    const suffix = remaining > 0 ? ` +${remaining} ${moreText}` : '';
    const totalLength = prefix.length + testDisplay.length + suffix.length;
    
    if (totalLength > maxChars) break;
    
    display = testDisplay;
    count++;
  }
  
  const remaining = sizes.length - count;
  return {
    display: display,
    hasMore: remaining > 0,
    remaining: remaining
  };
};

const ProductImageCard: React.FC<ProductImageCardProps> = ({ productGroup, onClick, onOutfitSuggestion, loading = false, config }) => {
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [autoRotateEnabled, setAutoRotateEnabled] = useState(true);
  const { sendProductAction, sendProductActionTracking, isProcessing } = useConversation();

  const currentImage = productGroup.images[currentImageIndex];
  const hasMultipleImages = productGroup.images.length > 1;
  const isSourceProduct = currentImage.is_source_product === true;

  // Build srcset string for responsive images
  const srcsetString = buildSrcSetString(currentImage.srcset);

  // Auto-rotate configuration from config
  const autoRotateFeatureEnabled = config?.image_carousel?.enable_auto_rotate !== false; // Default: true
  const autoRotateInterval = config?.image_carousel?.auto_rotate_interval ?? 3000; // Default: 3000ms

  // Auto-rotate through images if enabled and there are multiple images
  useEffect(() => {
    if (!hasMultipleImages || !autoRotateEnabled || !autoRotateFeatureEnabled) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % productGroup.images.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [hasMultipleImages, autoRotateEnabled, autoRotateFeatureEnabled, autoRotateInterval, productGroup.images.length]);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleClick = useCallback(() => {
    // Track product click
    sendProductActionTracking(
      'product_click',
      'recommendation',
      currentImage.product_url || '',
      currentImage.product_title || currentImage.alt_text,
      undefined,
      currentImage.price || undefined,
      currentImage.metadata?.price != null ? Number(currentImage.metadata.price) : undefined
    );
    
    if (onClick && !imageError) {
      // Include all product images in metadata for QuickView navigation
      const imageWithAllProducts: ImageMetadata = {
        ...currentImage,
        metadata: {
          ...currentImage.metadata,
          productImages: productGroup.images.map(img => img.url),
        }
      };
      onClick(imageWithAllProducts);
    } else if (!imageError) {
      // Fallback: open product URL directly
      window.open(currentImage.product_url, '_blank', 'noopener,noreferrer');
    }
  }, [onClick, currentImage, imageError, sendProductActionTracking, productGroup.images]);

  const handleMouseEnter = () => {
    setAutoRotateEnabled(false);
  };

  const handleMouseLeave = () => {
    setAutoRotateEnabled(true);
  };

  const handleImageNavigation = (direction: 'prev' | 'next') => {
    if (!hasMultipleImages) return;
    
    if (direction === 'prev') {
      setCurrentImageIndex((prev) => prev === 0 ? productGroup.images.length - 1 : prev - 1);
    } else {
      setCurrentImageIndex((prev) => (prev + 1) % productGroup.images.length);
    }
    setAutoRotateEnabled(false);
  };

  if (loading) {
    return (
      <Card sx={{ width: 200, height: 310, m: 0 }}>
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Box sx={{ p: 1 }}>
          <Skeleton width="60%" />
          <Skeleton width="40%" />
        </Box>
      </Card>
    );
  }

  if (imageError) {
    return (
      <Card sx={{ width: 200, height: 310, m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Image unavailable
        </Typography>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        width: 200, 
        height: 310, 
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        // Source product highlight styling
        border: isSourceProduct ? '2px solid' : 'none',
        borderColor: isSourceProduct ? 'primary.main' : 'transparent',
        bgcolor: isSourceProduct ? 'primary.50' : 'background.paper',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isSourceProduct ? 6 : 4,
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip 
        title={config?.image_carousel?.show_tooltip !== false ? `Click to view product: ${
          (currentImage.product_title && currentImage.product_title.trim()) || 
          (currentImage.alt_text && currentImage.alt_text.trim()) || 
          (currentImage.title && currentImage.title.trim()) || 
          'Product image'
        }` : ''} 
        arrow
        placement="top"
        disableHoverListener={config?.image_carousel?.show_tooltip === false}
      >
        <CardActionArea onClick={handleClick} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}>
          <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
            {imageLoading && (
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height="100%" 
                sx={{ position: 'absolute', top: 0, left: 0 }}
              />
            )}
            <CardMedia
              component="img"
              height="200"
              image={currentImage.url}
              alt={currentImage.alt_text || 'Product image'}
              onLoad={handleImageLoad}
              onError={handleImageError}
              srcSet={srcsetString}
              sizes="200px"
              sx={{
                objectFit: 'cover',
                opacity: imageLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
            />
            
            {/* Source Product Badge - "Your Item" indicator */}
            {isSourceProduct && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'primary.main',
                  borderRadius: '12px',
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Star sx={{ fontSize: 12, color: 'white' }} />
                <Typography variant="caption" sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 600 }}>
                  Your Item
                </Typography>
              </Box>
            )}

            {/* Basket button - bottom right corner */}
            <Tooltip title="Add to cart" arrow placement="top">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  // Track add to cart click
                  sendProductActionTracking(
                    'add_to_cart',
                    'recommendation',
                    currentImage.product_url || '',
                    currentImage.product_title || currentImage.alt_text,
                    undefined,
                    currentImage.price || undefined,
                    currentImage.metadata?.price != null ? Number(currentImage.metadata.price) : undefined
                  );
                  window.open(currentImage.product_url, '_blank', 'noopener,noreferrer');
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  bottom: 8,
                  bgcolor: 'transparent',
                  color: 'text.primary',
                  width: 32,
                  height: 32,
                boxShadow: 0,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s',
              }}
            >
              <ShoppingBagOutlined sx={{ fontSize: 18 }} />
            </IconButton>
            </Tooltip>

            {/* Image navigation arrows (visible on hover for desktop, always visible on mobile) */}
            {hasMultipleImages && (
              <>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageNavigation('prev');
                  }}
                  sx={{
                    position: 'absolute',
                    left: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    width: 24,
                    height: 24,
                    opacity: isMobileDevice ? 1 : 0,
                    transition: 'opacity 0.2s',
                    '.MuiCard-root:hover &': {
                      opacity: 1,
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                    }
                  }}
                >
                  <ChevronLeft sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageNavigation('next');
                  }}
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    width: 24,
                    height: 24,
                    opacity: isMobileDevice ? 1 : 0,
                    transition: 'opacity 0.2s',
                    '.MuiCard-root:hover &': {
                      opacity: 1,
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                    }
                  }}
                >
                  <ChevronRight sx={{ fontSize: 16 }} />
                </IconButton>
              </>
            )}
            
            {/* Overlay Dots indicator for multiple images */}
            {hasMultipleImages && config?.image_carousel?.product_card_dots_position === 'overlay' && (
              <Box 
                sx={{ 
                  position: 'absolute',
                  bottom: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: `${config?.image_carousel?.product_card_dots_gap ?? 4}px`,
                  bgcolor: config?.image_carousel?.product_card_dots_background_color ?? 'rgba(0,0,0,0.4)',
                  borderRadius: '10px',
                  px: 1,
                  py: 0.5,
                }}
              >
                {productGroup.images.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: config?.image_carousel?.product_card_dots_size ?? 6,
                      height: config?.image_carousel?.product_card_dots_size ?? 6,
                      borderRadius: '50%',
                      bgcolor: index === currentImageIndex 
                        ? (config?.image_carousel?.product_card_dots_active_color ?? '#ffffff')
                        : (config?.image_carousel?.product_card_dots_inactive_color ?? 'rgba(255,255,255,0.5)'),
                      transition: 'background-color 0.2s',
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ p: 1, height: 80, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.2,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {(() => {
                  // Prioritize product_title, but only if it has meaningful content (not just whitespace)
                  const displayTitle = 
                    (currentImage.product_title && currentImage.product_title.trim()) || 
                    (currentImage.alt_text && currentImage.alt_text.trim()) || 
                    (currentImage.title && currentImage.title.trim()) || 
                    'Product';
                  console.log('DEBUG ProductImageCard:', {
                    product_title: currentImage.product_title,
                    alt_text: currentImage.alt_text,
                    title: currentImage.title,
                    displayedTitle: displayTitle
                  });
                  return displayTitle;
                })()}
              </Typography>
              
              {/* Product details from LLM structured response */}
              <Box sx={{ mt: 0.25 }}>
                {config?.image_carousel?.show_price !== false && currentImage.price && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  >
                    {currentImage.price}
                  </Typography>
                )}
                {/* Combined Color and Gender on same line */}
                {((config?.image_carousel?.show_color !== false && currentImage.color) || 
                  (config?.image_carousel?.show_gender !== false && extractMetadataField<string>(currentImage, 'gender'))) && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      color: 'text.secondary',
                      fontSize: '0.65rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {config?.image_carousel?.show_color !== false && currentImage.color && (
                      <>{config?.ui_text?.image_carousel_color_label ? `${config.ui_text.image_carousel_color_label} ` : ''}{currentImage.color}</>
                    )}
                    {config?.image_carousel?.show_color !== false && currentImage.color && 
                     config?.image_carousel?.show_gender !== false && extractMetadataField<string>(currentImage, 'gender') && (
                      <> {config?.ui_text?.image_carousel_attribute_separator || '|'} </>
                    )}
                    {config?.image_carousel?.show_gender !== false && extractMetadataField<string>(currentImage, 'gender') && (
                      <>{config?.ui_text?.image_carousel_gender_label ? `${config.ui_text.image_carousel_gender_label} ` : ''}{extractMetadataField<string>(currentImage, 'gender')}</>
                    )}
                  </Typography>
                )}
                {config?.image_carousel?.show_sizes !== false && currentImage.sizes && currentImage.sizes.length > 0 && (() => {
                  const sizesLabel = config?.ui_text?.image_carousel_sizes_label ?? 'Sizes:';
                  const moreText = config?.ui_text?.image_carousel_sizes_more_text ?? 'more';
                  const formatted = formatSizesForDisplay(currentImage.sizes, 25, sizesLabel, moreText);
                  return (
                    <Tooltip 
                      title={formatted.hasMore ? `All sizes: ${currentImage.sizes.join(', ')}` : ''}
                      arrow
                      placement="top"
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          color: 'text.secondary',
                          fontSize: '0.65rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: formatted.hasMore ? 'help' : 'default',
                        }}
                      >
                        {sizesLabel ? `${sizesLabel} ` : ''}{formatted.display}{formatted.hasMore ? ` +${formatted.remaining} ${moreText}` : ''}
                      </Typography>
                    </Tooltip>
                  );
                })()}
                {config?.image_carousel?.show_description === true && currentImage.product_desc && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.65rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mt: 0.25,
                    }}
                  >
                    {currentImage.product_desc}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </CardActionArea>
      </Tooltip>
        
      {/* Product Action Buttons - Hidden for source products */}
      {!isSourceProduct && (
          <Box 
            sx={{ 
              px: 0.5, 
              pb: 0.75,
              pt: 0.5,
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Tooltip title="Get outfit ideas" arrow placement="top">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={config?.image_carousel?.show_button_icons !== false ? <Checkroom sx={{ fontSize: 14 }} /> : undefined}
                  disabled={isProcessing}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Send ALL images for the product, not just the current one
                    const allProductImages = productGroup.images.map(img => ({
                      url: img.url,
                      width: img.width,
                      height: img.height,
                      alt_text: img.alt_text,
                      format: img.format,
                    }));
                    
                    const sourceProduct: ProductActionSourceProduct = {
                      product_url: currentImage.product_url,
                      product_title: currentImage.product_title || currentImage.alt_text || currentImage.title,
                      // Extract metadata fields for better context in outfit suggestions
                      item_type: extractMetadataField<string>(currentImage, 'item_type'),
                      style: extractMetadataField<string[]>(currentImage, 'style'),
                      silhouette: extractMetadataField<string>(currentImage, 'silhouette'),
                      color_palette: extractMetadataField<string[]>(currentImage, 'color_palette'),
                      gender: extractMetadataField<string>(currentImage, 'gender'),
                      season: extractMetadataField<string>(currentImage, 'season'),
                      product_desc: extractMetadataField<string>(currentImage, 'product_desc'),
                      // Include display fields (price, color, sizes) for source product display
                      price: currentImage.price,
                      color: currentImage.color,
                      sizes: currentImage.sizes,
                      // Include all images for displaying source product with rotation in outfit results
                      images: allProductImages,
                      // Also include primary image for backwards compatibility
                      image_url: currentImage.url,
                      image_width: currentImage.width,
                      image_height: currentImage.height,
                      image_alt_text: currentImage.alt_text,
                    };
                    
                    // Track button click
                    sendProductActionTracking(
                      'button_click',
                      'outfit_suggestion',
                      currentImage.product_url || '',
                      currentImage.product_title || currentImage.alt_text,
                      undefined,
                      currentImage.price || undefined,
                      currentImage.metadata?.price != null ? Number(currentImage.metadata.price) : undefined
                    );
                    
                    if (onOutfitSuggestion) {
                      onOutfitSuggestion(sourceProduct);
                    } else {
                      // Default: use sendProductAction from context
                      sendProductAction('outfit_suggestion', sourceProduct, (response) => {
                        console.log('Outfit suggestion response:', response);
                        // Response handling is done via callback - could show a modal, etc.
                      });
                    }
                  }}
                  sx={{
                    bgcolor: config?.image_carousel?.outfit_button_bg_color || 'primary.main',
                    color: config?.image_carousel?.outfit_button_text_color || 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1,
                    minWidth: 'auto',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    borderRadius: '20px',
                    boxShadow: 'none',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      marginLeft: 0,
                    },
                    '&:hover': {
                      bgcolor: config?.image_carousel?.outfit_button_bg_color || 'primary.dark',
                      boxShadow: 1,
                      filter: 'brightness(0.9)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    }
                  }}
                >
                  {config?.ui_text?.image_carousel_outfit_button_text || 'Outfit Ideas'}
                </Button>
              </span>
            </Tooltip>
          
            <Tooltip title="Find similar items" arrow placement="top">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={config?.image_carousel?.show_button_icons !== false ? <Tune sx={{ fontSize: 14 }} /> : undefined}
                  disabled={isProcessing}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Send ALL images for the product, not just the current one
                    const allProductImages = productGroup.images.map(img => ({
                      url: img.url,
                      width: img.width,
                      height: img.height,
                      alt_text: img.alt_text,
                      format: img.format,
                    }));
                    
                    const sourceProduct: ProductActionSourceProduct = {
                      product_url: currentImage.product_url,
                      product_title: currentImage.product_title || currentImage.alt_text || currentImage.title,
                      // Extract metadata fields for similarity matching
                      item_type: extractMetadataField<string>(currentImage, 'item_type'),
                      style: extractMetadataField<string[]>(currentImage, 'style'),
                      silhouette: extractMetadataField<string>(currentImage, 'silhouette'),
                      color_palette: extractMetadataField<string[]>(currentImage, 'color_palette'),
                      gender: extractMetadataField<string>(currentImage, 'gender'),
                      season: extractMetadataField<string>(currentImage, 'season'),
                      product_desc: extractMetadataField<string>(currentImage, 'product_desc'),
                      // Include display fields (price, color, sizes) for source product display
                      price: currentImage.price,
                      color: currentImage.color,
                      sizes: currentImage.sizes,
                      // Include all images for displaying source product reference
                      images: allProductImages,
                      // Also include primary image for backwards compatibility
                      image_url: currentImage.url,
                      image_width: currentImage.width,
                      image_height: currentImage.height,
                      image_alt_text: currentImage.alt_text,
                    };
                    
                    // Track button click
                    sendProductActionTracking(
                      'button_click',
                      'more_like_this',
                      currentImage.product_url || '',
                      currentImage.product_title || currentImage.alt_text,
                      undefined,
                      currentImage.price || undefined,
                      currentImage.metadata?.price != null ? Number(currentImage.metadata.price) : undefined
                    );
                    
                    sendProductAction('more_like_this', sourceProduct, (response) => {
                      console.log('More like this response:', response);
                    });
                  }}
                  sx={{
                    bgcolor: config?.image_carousel?.similar_button_bg_color || 'success.main',
                    color: config?.image_carousel?.similar_button_text_color || 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1,
                    minWidth: 'auto',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    borderRadius: '20px',
                    boxShadow: 'none',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      marginLeft: 0,
                    },
                    '&:hover': {
                      bgcolor: config?.image_carousel?.similar_button_bg_color || 'success.dark',
                      boxShadow: 1,
                      filter: 'brightness(0.9)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    }
                  }}
                >
                  {config?.ui_text?.image_carousel_similar_button_text || 'More Like This'}
                </Button>
              </span>
            </Tooltip>
        </Box>
      )}
    </Card>
  );
};

const ImageCard: React.FC<ImageCardProps> = ({ image, onClick, onOutfitSuggestion, loading = false, config }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const { sendProductAction, sendProductActionTracking, isProcessing } = useConversation();
  const isSourceProduct = image.is_source_product === true;

  // Build srcset string for responsive images
  const srcsetString = buildSrcSetString(image.srcset);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleClick = useCallback(() => {
    // Track product click
    sendProductActionTracking(
      'product_click',
      'recommendation',
      image.product_url || '',
      image.product_title || image.alt_text,
      undefined,
      image.price || undefined,
      image.metadata?.price != null ? Number(image.metadata.price) : undefined
    );
    
    if (onClick && !imageError) {
      onClick(image);
    } else if (!imageError) {
      // Fallback: open product URL directly
      window.open(image.product_url, '_blank', 'noopener,noreferrer');
    }
  }, [onClick, image, imageError, sendProductActionTracking]);

  if (loading) {
    return (
      <Card sx={{ width: 200, height: 310, m: 0 }}>
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Box sx={{ p: 1 }}>
          <Skeleton width="60%" />
          <Skeleton width="40%" />
        </Box>
      </Card>
    );
  }

  if (imageError) {
    return (
      <Card sx={{ width: 200, height: 310, m: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Image unavailable
        </Typography>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        width: 200, 
        height: 310, 
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        // Source product highlight styling
        border: isSourceProduct ? '2px solid' : 'none',
        borderColor: isSourceProduct ? 'primary.main' : 'transparent',
        bgcolor: isSourceProduct ? 'primary.50' : 'background.paper',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isSourceProduct ? 6 : 4,
        }
      }}
    >
      <Tooltip 
        title={config?.image_carousel?.show_tooltip !== false ? `Click to view product: ${
          (image.product_title && image.product_title.trim()) || 
          (image.alt_text && image.alt_text.trim()) || 
          (image.title && image.title.trim()) || 
          'Product image'
        }` : ''} 
        arrow
        placement="top"
        disableHoverListener={config?.image_carousel?.show_tooltip === false}
      >
        <CardActionArea onClick={handleClick} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}>
          <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
            {imageLoading && (
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height="100%" 
                sx={{ position: 'absolute', top: 0, left: 0 }}
              />
            )}
            <CardMedia
              component="img"
              height="200"
              image={image.url}
              alt={image.alt_text || 'Product image'}
              onLoad={handleImageLoad}
              onError={handleImageError}
              srcSet={srcsetString}
              sizes="200px"
              sx={{
                objectFit: 'cover',
                opacity: imageLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
            />
            
            {/* Source Product Badge - "Your Item" indicator */}
            {isSourceProduct && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'primary.main',
                  borderRadius: '12px',
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Star sx={{ fontSize: 12, color: 'white' }} />
                <Typography variant="caption" sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 600 }}>
                  Your Item
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ p: 1, height: 80, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.2,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {(() => {
                  // Prioritize product_title, but only if it has meaningful content (not just whitespace)
                  const displayTitle = 
                    (image.product_title && image.product_title.trim()) || 
                    (image.alt_text && image.alt_text.trim()) || 
                    (image.title && image.title.trim()) || 
                    'Product';
                  console.log('DEBUG ImageCard:', {
                    product_title: image.product_title,
                    alt_text: image.alt_text,
                    title: image.title,
                    displayedTitle: displayTitle
                  });
                  return displayTitle;
                })()}
              </Typography>
              
              {/* Product details from LLM structured response */}
              <Box sx={{ mt: 0.25 }}>
                {image.price && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  >
                    {image.price}
                  </Typography>
                )}
                {/* Combined Color and Gender on same line */}
                {((config?.image_carousel?.show_color !== false && image.color) || 
                  (config?.image_carousel?.show_gender !== false && extractMetadataField<string>(image, 'gender'))) && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      color: 'text.secondary',
                      fontSize: '0.65rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {config?.image_carousel?.show_color !== false && image.color && (
                      <>{config?.ui_text?.image_carousel_color_label ? `${config.ui_text.image_carousel_color_label} ` : ''}{image.color}</>
                    )}
                    {config?.image_carousel?.show_color !== false && image.color && 
                     config?.image_carousel?.show_gender !== false && extractMetadataField<string>(image, 'gender') && (
                      <> {config?.ui_text?.image_carousel_attribute_separator || '|'} </>
                    )}
                    {config?.image_carousel?.show_gender !== false && extractMetadataField<string>(image, 'gender') && (
                      <>{config?.ui_text?.image_carousel_gender_label ? `${config.ui_text.image_carousel_gender_label} ` : ''}{extractMetadataField<string>(image, 'gender')}</>
                    )}
                  </Typography>
                )}
                {config?.image_carousel?.show_sizes !== false && image.sizes && image.sizes.length > 0 && (() => {
                  const sizesLabel = config?.ui_text?.image_carousel_sizes_label ?? 'Sizes:';
                  const moreText = config?.ui_text?.image_carousel_sizes_more_text ?? 'more';
                  const formatted = formatSizesForDisplay(image.sizes, 25, sizesLabel, moreText);
                  return (
                    <Tooltip 
                      title={formatted.hasMore ? `All sizes: ${image.sizes.join(', ')}` : ''}
                      arrow
                      placement="top"
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          color: 'text.secondary',
                          fontSize: '0.65rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: formatted.hasMore ? 'help' : 'default',
                        }}
                      >
                        {sizesLabel ? `${sizesLabel} ` : ''}{formatted.display}{formatted.hasMore ? ` +${formatted.remaining} ${moreText}` : ''}
                      </Typography>
                    </Tooltip>
                  );
                })()}
              </Box>
            </Box>
          </Box>
        </CardActionArea>
      </Tooltip>
        
      {/* Product Action Buttons - Hidden for source products */}
      {!isSourceProduct && (
          <Box 
            sx={{ 
              px: 0.5, 
              pb: 0.75,
              pt: 0.5,
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Tooltip title="Get outfit ideas" arrow placement="top">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={config?.image_carousel?.show_button_icons !== false ? <Checkroom sx={{ fontSize: 14 }} /> : undefined}
                  disabled={isProcessing}
                  onClick={(e) => {
                    e.stopPropagation();
                    const sourceProduct: ProductActionSourceProduct = {
                      product_url: image.product_url,
                      product_title: image.product_title || image.alt_text || image.title,
                      // Extract metadata fields for better context in outfit suggestions
                      item_type: extractMetadataField<string>(image, 'item_type'),
                      style: extractMetadataField<string[]>(image, 'style'),
                      silhouette: extractMetadataField<string>(image, 'silhouette'),
                      color_palette: extractMetadataField<string[]>(image, 'color_palette'),
                      gender: extractMetadataField<string>(image, 'gender'),
                      season: extractMetadataField<string>(image, 'season'),
                      product_desc: extractMetadataField<string>(image, 'product_desc'),
                      // Include display fields (price, color, sizes) for source product display
                      price: image.price,
                      color: image.color,
                      sizes: image.sizes,
                      // Include image data for displaying source product in outfit results
                      image_url: image.url,
                      image_width: image.width,
                      image_height: image.height,
                      image_alt_text: image.alt_text,
                    };
                    
                    // Track button click
                    sendProductActionTracking(
                      'button_click',
                      'outfit_suggestion',
                      image.product_url || '',
                      image.product_title || image.alt_text,
                      undefined,
                      image.price || undefined,
                      image.metadata?.price != null ? Number(image.metadata.price) : undefined
                    );
                    
                    if (onOutfitSuggestion) {
                      onOutfitSuggestion(sourceProduct);
                    } else {
                      sendProductAction('outfit_suggestion', sourceProduct, (response) => {
                        console.log('Outfit suggestion response:', response);
                      });
                    }
                  }}
                  sx={{
                    bgcolor: config?.image_carousel?.outfit_button_bg_color || 'primary.main',
                    color: config?.image_carousel?.outfit_button_text_color || 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1,
                    minWidth: 'auto',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    borderRadius: '20px',
                    boxShadow: 'none',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      marginLeft: 0,
                    },
                    '&:hover': {
                      bgcolor: config?.image_carousel?.outfit_button_bg_color || 'primary.dark',
                      boxShadow: 1,
                      filter: 'brightness(0.9)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    }
                  }}
                >
                  {config?.ui_text?.image_carousel_outfit_button_text || 'Outfit Ideas'}
                </Button>
              </span>
            </Tooltip>
          
            <Tooltip title="Find similar items" arrow placement="top">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={config?.image_carousel?.show_button_icons !== false ? <Tune sx={{ fontSize: 14 }} /> : undefined}
                  disabled={isProcessing}
                  onClick={(e) => {
                    e.stopPropagation();
                    const sourceProduct: ProductActionSourceProduct = {
                      product_url: image.product_url,
                      product_title: image.product_title || image.alt_text || image.title,
                      // Extract metadata fields for similarity matching
                      item_type: extractMetadataField<string>(image, 'item_type'),
                      style: extractMetadataField<string[]>(image, 'style'),
                      silhouette: extractMetadataField<string>(image, 'silhouette'),
                      color_palette: extractMetadataField<string[]>(image, 'color_palette'),
                      gender: extractMetadataField<string>(image, 'gender'),
                      season: extractMetadataField<string>(image, 'season'),
                      product_desc: extractMetadataField<string>(image, 'product_desc'),
                      // Include display fields (price, color, sizes) for source product display
                      price: image.price,
                      color: image.color,
                      sizes: image.sizes,
                      // Include image data for source product reference
                      image_url: image.url,
                      image_width: image.width,
                      image_height: image.height,
                      image_alt_text: image.alt_text,
                    };
                    
                    // Track button click
                    sendProductActionTracking(
                      'button_click',
                      'more_like_this',
                      image.product_url || '',
                      image.product_title || image.alt_text,
                      undefined,
                      image.price || undefined,
                      image.metadata?.price != null ? Number(image.metadata.price) : undefined
                    );
                    
                    sendProductAction('more_like_this', sourceProduct, (response) => {
                      console.log('More like this response:', response);
                    });
                  }}
                  sx={{
                    bgcolor: config?.image_carousel?.similar_button_bg_color || 'success.main',
                    color: config?.image_carousel?.similar_button_text_color || 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1,
                    minWidth: 'auto',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    borderRadius: '20px',
                    boxShadow: 'none',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      marginLeft: 0,
                    },
                    '&:hover': {
                      bgcolor: config?.image_carousel?.similar_button_bg_color || 'success.dark',
                      boxShadow: 1,
                      filter: 'brightness(0.9)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    }
                  }}
                >
                  {config?.ui_text?.image_carousel_similar_button_text || 'More Like This'}
                </Button>
              </span>
            </Tooltip>
        </Box>
      )}
    </Card>
  );
};
// Helper function to group images by product
const groupImagesByProduct = (images: ImageMetadata[]): ProductImageGroup[] => {
  const groups = new Map<string, ImageMetadata[]>();
  
  images.forEach(image => {
    const productKey = image.product_url || 'unknown';
    if (!groups.has(productKey)) {
      groups.set(productKey, []);
    }
    groups.get(productKey)!.push(image);
  });
  
  return Array.from(groups.entries()).map(([product_url, images]) => ({
    product_url,
    images,
    current_image_index: 0
  }));
};

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, citations = [], messageContent, onImageClick, onOutfitSuggestion }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { config } = useAppConfig();
  
  // View mode state (for 'both' display mode) - default to grid
  const [viewMode, setViewMode] = useState<'carousel' | 'grid'>('grid');
  
  // Grid pagination state
  const [gridPage, setGridPage] = useState(0);
  
  // DEBUG: Log incoming props
  console.log('[ImageCarousel] Component rendered with:', {
    imagesCount: images.length,
    citationsCount: citations.length,
    hasMessageContent: !!messageContent,
    messageContentPreview: messageContent ? messageContent.substring(0, 200) : 'N/A',
  });
  
  const [currentIndex, setCurrentIndex] = useState(0);

  // Touch swipe support
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  // Reset currentIndex when screen size changes to prevent carousel scrolling breaks
  useEffect(() => {
    setCurrentIndex(0);
  }, [isMobile, isTablet]);

  // Filter images based on citations or structured response
  // Supports "Streaming Product Reveal" - products appear as LLM mentions them during streaming
  const filteredImages = React.useMemo(() => {
    // Check if citation-based filtering is enabled (default: true for backward compatibility)
    const citationFilteringEnabled = config?.image_carousel?.enable_citation_based_filtering !== false;
    
    // If citation filtering is disabled, return all images (enriched if structured response exists)
    if (!citationFilteringEnabled) {
      return messageContent ? enrichImagesWithProductData(images, messageContent) : images;
    }
    
    // Helper function to clean URLs by removing unnecessary characters
    const cleanUrl = (url: string): string => {
      return url
        .trim()                    // Remove leading/trailing whitespace
        .replace(/[\n\r\t]/g, '')  // Remove newlines, carriage returns, tabs
        .replace(/\s+/g, ' ')      // Replace multiple spaces with single space
        .trim();                   // Final trim after space normalization
    };
    
    // === STREAMING PRODUCT REVEAL ===
    // Try to extract complete products from partial JSON during streaming
    // This enables products to appear as the LLM mentions them, not after streaming completes
    let streamingProducts: ProductReference[] = [];
    if (messageContent && messageContent.includes('"products"')) {
      streamingProducts = extractProductsFromPartialJSON(messageContent);
      if (streamingProducts.length > 0) {
        console.log('[ImageCarousel] DEBUG - Streaming products extracted:', streamingProducts.length);
      }
    }
    
    // Check if messageContent is a complete structured response
    const isStructured = messageContent ? isStructuredResponse(messageContent) : false;
    
    // === MAIN FILTERING LOGIC ===
    // Use streaming products if found, OR complete structured response
    if (streamingProducts.length > 0 || isStructured) {
      // Get product URLs: prefer streaming extraction (works during streaming), 
      // fall back to complete parsing (works after streaming completes)
      const productUrls = streamingProducts.length > 0
        ? streamingProducts.map(p => p.product_url).filter(Boolean)
        : extractProductUrlsFromStructured(messageContent || '');
      
      console.log('[ImageCarousel] DEBUG - Product URLs for filtering:', productUrls.length, 
        'source:', streamingProducts.length > 0 ? 'streaming' : 'complete');
      
      // If no products found, return only source product images
      if (productUrls.length === 0) {
        console.log('[ImageCarousel] DEBUG - No product URLs, returning source products only');
        const sourceProducts = images.filter(image => image.is_source_product === true);
        return sourceProducts;
      }
      
      const cleanedProductUrls = new Set(
        productUrls
          .map(url => cleanUrl(url))
          .filter(url => url.length > 0)
      );
      
      console.log('[ImageCarousel] DEBUG - Cleaned product URLs:', Array.from(cleanedProductUrls));
      
      // Separate source products from complementary items
      const sourceProducts = images.filter(image => image.is_source_product === true);
      
      // Filter images where cleaned product_url matches products from LLM
      const complementaryItems = images.filter(image => {
        const cleanedImageUrl = cleanUrl(image.product_url || '');
        return cleanedImageUrl.length > 0 && cleanedProductUrls.has(cleanedImageUrl);
      });
      
      // Always include source products FIRST, then complementary items
      const filtered = [...sourceProducts, ...complementaryItems];
      
      console.log('[ImageCarousel] DEBUG - Source products:', sourceProducts.length);
      console.log('[ImageCarousel] DEBUG - Complementary items:', complementaryItems.length);
      console.log('[ImageCarousel] DEBUG - Total filtered images:', filtered.length);
      
      // Enrich filtered images with product data
      // Use streaming products if available (more responsive), otherwise use complete parsing
      if (streamingProducts.length > 0) {
        const enriched = enrichImagesFromStreamingProducts(filtered, streamingProducts);
        console.log('[ImageCarousel] DEBUG - Enriched from streaming products');
        return enriched;
      }
      const enriched = messageContent ? enrichImagesWithProductData(filtered, messageContent) : filtered;
      console.log('[ImageCarousel] DEBUG - Enriched from complete response');
      return enriched;
    }
    
    // === CITATION FALLBACK PATH ===
    // Used when structured output is disabled or response is not JSON format
    if (citations.length === 0) {
      console.log('[ImageCarousel] DEBUG - No streaming products, structured response, or citations - returning empty');
      return [];
    }
    
    // Extract and clean URLs from citations
    const citationUrls = new Set(
      citations
        .map(citation => cleanUrl(citation.url))
        .filter(url => url.length > 0) // Remove empty URLs after cleaning
    );
    
    console.log('[ImageCarousel] DEBUG - Citation URLs:', Array.from(citationUrls));
    
    // Filter images where cleaned product_url matches any cleaned citation URL
    const filtered = images.filter(image => {
      const cleanedProductUrl = cleanUrl(image.product_url || '');
      return cleanedProductUrl.length > 0 && citationUrls.has(cleanedProductUrl);
    });
    
    console.log('[ImageCarousel] DEBUG - Filtered images (citations):', filtered.length, filtered);
    return filtered;
  }, [images, citations, messageContent, config?.image_carousel?.enable_citation_based_filtering]);

  // Group filtered images by product if grouping is enabled
  const productGroups = groupImagesByProduct(filteredImages);
  const shouldGroupByProduct = productGroups.length < filteredImages.length; // If grouping reduces the count, we have duplicates

  // Helper function to check if title should be hidden
  const shouldHideTitle = () => {
    const title = config?.ui_text?.image_carousel_title;
    const fontSize = config?.ui_theme?.image_carousel_title_font_size;
    
    // Hide if title is undefined, empty, or explicitly set to hide
    if (title === undefined || title === null || title === "") {
      return true;
    }
    
    // Hide if font size is set to zero (various formats)
    if (fontSize === "0" || fontSize === "0rem" || fontSize === "0.0rem" || 
        fontSize === "0px" || fontSize === "0em" || fontSize === "0pt") {
      return true;
    }
    
    return false;
  };

  // Determine how many items to show at once
  const getVisibleCount = () => {
    if (isMobile) {
      return config?.image_carousel?.mobile_visible_count ?? 1;
    }
    if (isTablet) {
      return config?.image_carousel?.tablet_visible_count ?? 2;
    }
    const itemCount = shouldGroupByProduct ? productGroups.length : filteredImages.length;
    const desktopCount = config?.image_carousel?.desktop_visible_count ?? 4;
    return Math.min(desktopCount, itemCount);
  };

  const visibleCount = getVisibleCount();
  const itemCount = shouldGroupByProduct ? productGroups.length : filteredImages.length;
  const totalPages = Math.ceil(itemCount / visibleCount);

  // Reset to valid page when items or visible count changes
  useEffect(() => {
    const maxIndex = totalPages - 1;
    if (currentIndex > maxIndex) {
      setCurrentIndex(Math.max(0, maxIndex));
    }
  }, [totalPages, currentIndex]);

  // All hooks must be called at the top level
  const handleImageClick = useCallback((image: ImageMetadata) => {
    if (onImageClick) {
      onImageClick(image);
    } else {
      // Default behavior: open product URL
      window.open(image.product_url, '_blank', 'noopener,noreferrer');
    }
  }, [onImageClick]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  const handleMobilePrevious = useCallback(() => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  }, []);

  const handleMobileNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(itemCount - 1, prev + 1));
  }, [itemCount]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevious, handleNext]);

  // HIDE if no matching images
  // Determine display mode from configuration (must be before conditional return to maintain hook order)
  const displayMode = config?.image_carousel?.display_mode || 'carousel'; // Options: 'carousel', 'grid', 'both'
  const enableViewToggle = config?.image_carousel?.enable_view_toggle !== false;
  
  // Determine active view (for 'both' mode, use viewMode state; otherwise use displayMode)
  const activeView: 'carousel' | 'grid' = displayMode === 'both' ? viewMode : (displayMode === 'grid' ? 'grid' : 'carousel');
  
  // Grid configuration (must be before conditional return to maintain hook order)
  const gridGap = config?.image_carousel?.grid_gap ?? 16;
  const gridProductsPerPage = config?.image_carousel?.grid_products_per_page ?? 20;
  const gridShowTitle = config?.image_carousel?.grid_show_title ?? false;
  
  // Grid pagination calculations
  const gridTotalPages = Math.ceil(itemCount / gridProductsPerPage);
  const gridStartIndex = gridPage * gridProductsPerPage;
  const gridEndIndex = Math.min(gridStartIndex + gridProductsPerPage, itemCount);
  
  // Get items for current grid page
  const gridPageItems = shouldGroupByProduct 
    ? productGroups.slice(gridStartIndex, gridEndIndex)
    : filteredImages.slice(gridStartIndex, gridEndIndex);
  
  // Reset grid page when total pages change (must be before conditional return to maintain hook order)
  useEffect(() => {
    if (gridPage >= gridTotalPages && gridTotalPages > 0) {
      setGridPage(Math.max(0, gridTotalPages - 1));
    }
  }, [gridTotalPages, gridPage]);

  // === VISIBILITY LOGIC ===
  // Supports Streaming Product Reveal: show carousel as soon as products are detected in streaming JSON
  const citationFilteringEnabled = config?.image_carousel?.enable_citation_based_filtering !== false;
  
  // Check if we have streaming products (works during streaming, before JSON is complete)
  const hasStreamingProducts = messageContent && messageContent.includes('"products"') 
    ? extractProductsFromPartialJSON(messageContent).length > 0 
    : false;
  
  // Check if message uses complete structured response format
  const isUsingStructuredResponse = messageContent ? isStructuredResponse(messageContent) : false;
  
  // Determine visibility:
  // - If streaming products detected OR structured response complete: show if we have filtered images
  // - Otherwise (citation mode): need citations AND matches
  const shouldHide = (hasStreamingProducts || isUsingStructuredResponse)
    ? filteredImages.length === 0  // Products context: hide only if no matches
    : (citationFilteringEnabled && citations.length === 0) || filteredImages.length === 0;  // Citation mode
  
  console.log('[ImageCarousel] DEBUG - Visibility check:', {
    filteredImagesLength: filteredImages.length,
    citationsLength: citations.length,
    citationFilteringEnabled,
    hasStreamingProducts,
    isUsingStructuredResponse,
    shouldHide
  });
  
  // Debug logging for display mode
  console.log('[ImageCarousel] Display mode configuration:', {
    displayMode,
    activeView,
    itemCount,
    filteredImagesLength: filteredImages.length,
    productGroupsLength: productGroups.length,
    shouldGroupByProduct
  });
  
  if (shouldHide) {
    return null;
  }
  
  // Handler for view mode toggle
  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'carousel' | 'grid' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
      // Reset pagination when switching views
      setCurrentIndex(0);
      setGridPage(0);
    }
  };
  
  // Grid navigation handlers
  const handleGridPreviousPage = () => {
    setGridPage(prev => Math.max(0, prev - 1));
  };
  
  const handleGridNextPage = () => {
    setGridPage(prev => Math.min(gridTotalPages - 1, prev + 1));
  };
  
  // Render view toggle buttons (only for 'both' mode)
  const renderViewToggle = () => {
    if (displayMode !== 'both' || !enableViewToggle) {
      return null;
    }
    
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 0.75,
              py: 0.25,
              fontSize: '0.7rem',
              textTransform: 'none',
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 'auto',
            }
          }}
        >
          <ToggleButton value="carousel" aria-label="carousel view">
            <ViewCarousel sx={{ mr: 0.25, fontSize: '0.9rem' }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Carousel</Typography>
          </ToggleButton>
          <ToggleButton value="grid" aria-label="grid view">
            <GridView sx={{ mr: 0.25, fontSize: '0.9rem' }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Grid</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  };
  
  // Render grid view
  const renderGridView = () => {
    return (
      <Fade in={true} timeout={300} style={{ width: '100%' }}>
        <Box sx={{ mb: 2, width: '100%', minWidth: 0 }}>
          {/* Optional Title for Grid */}
          {gridShowTitle && !shouldHideTitle() && (
            <Typography variant="h6" gutterBottom sx={{ 
              color: config?.ui_theme?.image_carousel_title_text || 'text.primary', 
              fontWeight: 600,
              fontSize: config?.ui_theme?.image_carousel_title_font_size || '1.25rem',
              backgroundColor: config?.ui_theme?.image_carousel_title_background || 'transparent',
              padding: config?.ui_theme?.image_carousel_title_background !== 'transparent' ? '4px 8px' : 0,
              borderRadius: config?.ui_theme?.image_carousel_title_background !== 'transparent' ? 1 : 0,
              mb: 2,
            }}>
              {config?.ui_text?.image_carousel_title}
            </Typography>
          )}
          
          {/* Grid Container */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 200px))',
              gap: `${gridGap}px`,
              justifyContent: 'center',
              justifyItems: 'center',
              width: '100%',
            }}
          >
            {shouldGroupByProduct ? (
              gridPageItems.map((productGroup, index) => (
                <ProductImageCard
                  key={`grid-product-${productGroup.product_url}-${gridStartIndex + index}`}
                  productGroup={productGroup as ProductImageGroup}
                  onClick={handleImageClick}
                  onOutfitSuggestion={onOutfitSuggestion}
                  config={config}
                />
              ))
            ) : (
              gridPageItems.map((image, index) => (
                <ImageCard
                  key={`grid-${(image as ImageMetadata).url}-${gridStartIndex + index}`}
                  image={image as ImageMetadata}
                  onClick={handleImageClick}
                  onOutfitSuggestion={onOutfitSuggestion}
                  config={config}
                />
              ))
            )}
          </Box>
          
          {/* Grid Pagination Controls */}
          {gridTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
              <Button
                onClick={handleGridPreviousPage}
                disabled={gridPage === 0}
                variant="outlined"
                size="small"
                startIcon={<ChevronLeft sx={{ fontSize: '0.9rem' }} />}
                sx={{
                  px: 0.75,
                  py: 0.25,
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  minWidth: 'auto',
                }}
              >
                Previous
              </Button>
              
              <Typography variant="body2" sx={{ minWidth: '80px', textAlign: 'center', fontSize: '0.7rem' }}>
                Page {gridPage + 1} of {gridTotalPages}
              </Typography>
              
              <Button
                onClick={handleGridNextPage}
                disabled={gridPage >= gridTotalPages - 1}
                variant="outlined"
                size="small"
                endIcon={<ChevronRight sx={{ fontSize: '0.9rem' }} />}
                sx={{
                  px: 0.75,
                  py: 0.25,
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  minWidth: 'auto',
                }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      </Fade>
    );
  };

  // If grid view is active, render grid view
  if (activeView === 'grid') {
    console.log('[ImageCarousel] Rendering grid view:', {
      gridPage,
      gridTotalPages,
      gridStartIndex,
      gridEndIndex,
      gridPageItemsLength: gridPageItems.length
    });
    
    return (
      <Box sx={{ width: '100%' }}>
        {renderViewToggle()}
        {renderGridView()}
      </Box>
    );
  }

  // Otherwise render carousel view
  // Mobile-specific carousel implementation
  if (isMobile) {
    return (
      <>
        {renderViewToggle()}
        <Fade in={true} timeout={300}>
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            {/* Conditionally render title only if it should be visible */}
            {!shouldHideTitle() && (
              <Typography variant="h6" gutterBottom sx={{ 
                color: config?.ui_theme?.image_carousel_title_text || 'text.primary', 
                fontWeight: 600,
                fontSize: config?.ui_theme?.image_carousel_title_font_size || '1.25rem',
                backgroundColor: config?.ui_theme?.image_carousel_title_background || 'transparent',
              padding: config?.ui_theme?.image_carousel_title_background !== 'transparent' ? '4px 8px' : 0,
              borderRadius: config?.ui_theme?.image_carousel_title_background !== 'transparent' ? 1 : 0,
            }}>
              {config?.ui_text?.image_carousel_title}
            </Typography>
          )}
          
          <Box 
            sx={{ position: 'relative', overflow: 'hidden' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Mobile Navigation buttons */}
            {itemCount > 1 && (
              <>
                <IconButton
                  onClick={handleMobilePrevious}
                  disabled={currentIndex === 0}
                  aria-label="Previous image"
                  sx={{
                    position: 'absolute',
                    left: -8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'white',
                    boxShadow: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { 
                      bgcolor: 'grey.100',
                      boxShadow: 3,
                      transform: 'translateY(-50%) scale(1.1)',
                    },
                    '&:disabled': { 
                      opacity: 0.3,
                      cursor: 'not-allowed',
                    },
                    '&:active:not(:disabled)': {
                      transform: 'translateY(-50%) scale(0.95)',
                    }
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                
                <IconButton
                  onClick={handleMobileNext}
                  disabled={currentIndex >= itemCount - 1}
                  aria-label="Next image"
                  sx={{
                    position: 'absolute',
                    right: -8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'white',
                    boxShadow: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { 
                      bgcolor: 'grey.100',
                      boxShadow: 3,
                      transform: 'translateY(-50%) scale(1.1)',
                    },
                    '&:disabled': { 
                      opacity: 0.3,
                      cursor: 'not-allowed',
                    },
                    '&:active:not(:disabled)': {
                      transform: 'translateY(-50%) scale(0.95)',
                    }
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </>
            )}

            {/* Mobile Items container - show one item at a time */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '300px', // Fixed height for mobile
                overflow: 'hidden',
              }}
            >
              {shouldGroupByProduct ? (
                productGroups.map((productGroup, index) => (
                  <Box
                    key={`mobile-product-${productGroup.product_url}-${index}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transform: `translateX(${(index - currentIndex) * 100}%)`,
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  >
                    <ProductImageCard
                      productGroup={productGroup}
                      onClick={handleImageClick}
                      onOutfitSuggestion={onOutfitSuggestion}
                      config={config}
                    />
                  </Box>
                ))
              ) : (
                filteredImages.map((image, index) => (
                  <Box
                    key={`mobile-${image.url}-${index}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transform: `translateX(${(index - currentIndex) * 100}%)`,
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  >
                    <ImageCard
                      image={image}
                      onClick={handleImageClick}
                      onOutfitSuggestion={onOutfitSuggestion}
                      config={config}
                    />
                  </Box>
                ))
              )}
            </Box>

            {/* Mobile Dots indicator */}
            {itemCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 0.5 }}>
                {Array.from({ length: itemCount }).map((_, index) => (
                  <Box
                    key={`mobile-dot-${index}`}
                    onClick={() => setCurrentIndex(index)}
                    role="button"
                    aria-label={`Go to image ${index + 1}`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCurrentIndex(index);
                      }
                    }}
                    sx={{
                      width: index === currentIndex ? 20 : 8,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: index === currentIndex ? 'primary.main' : 'grey.300',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        bgcolor: index === currentIndex ? 'primary.dark' : 'grey.400',
                        transform: 'scale(1.2)',
                      },
                      '&:focus': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
      </>
    );
  }

  // Desktop/Tablet carousel implementation
  return (
    <>
      {renderViewToggle()}
      <Fade in={true} timeout={300}>
        <Box sx={{ 
          mb: 2, 
        p: 1.5, 
        bgcolor: 'grey.50', 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        {/* Conditionally render title only if it should be visible */}
        {!shouldHideTitle() && (
          <Typography variant="h6" gutterBottom sx={{ 
            color: config?.ui_theme?.image_carousel_title_text || 'text.primary', 
            fontWeight: 600,
            fontSize: config?.ui_theme?.image_carousel_title_font_size || '1.25rem',
            backgroundColor: config?.ui_theme?.image_carousel_title_background || 'transparent',
            padding: config?.ui_theme?.image_carousel_title_background !== 'transparent' ? '4px 8px' : 0,
            borderRadius: config?.ui_theme?.image_carousel_title_background !== 'transparent' ? 1 : 0,
          }}>
            {config?.ui_text?.image_carousel_title}
          </Typography>
        )}
        
        <Box 
          sx={{ position: 'relative', overflow: 'hidden' }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Navigation buttons */}
          {totalPages > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                aria-label="Previous page"
                sx={{
                  position: 'absolute',
                  left: -8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'white',
                  boxShadow: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { 
                    bgcolor: 'grey.100',
                    boxShadow: 3,
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  '&:disabled': { 
                    opacity: 0.3,
                    cursor: 'not-allowed',
                  },
                  '&:active:not(:disabled)': {
                    transform: 'translateY(-50%) scale(0.95)',
                  }
                }}
              >
                <ChevronLeft />
              </IconButton>
              
              <IconButton
                onClick={handleNext}
                disabled={currentIndex >= totalPages - 1}
                aria-label="Next page"
                sx={{
                  position: 'absolute',
                  right: -8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'white',
                  boxShadow: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { 
                    bgcolor: 'grey.100',
                    boxShadow: 3,
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  '&:disabled': { 
                    opacity: 0.3,
                    cursor: 'not-allowed',
                  },
                  '&:active:not(:disabled)': {
                    transform: 'translateY(-50%) scale(0.95)',
                  }
                }}
              >
                <ChevronRight />
              </IconButton>
            </>
          )}

          {/* Items container */}
          <Box
            sx={{
              display: 'flex',
              transition: 'transform 0.3s ease-in-out',
              transform: `translateX(-${currentIndex * 100}%)`,
              willChange: 'transform', // Optimize for animation performance
            }}
          >
            {shouldGroupByProduct ? (
              productGroups.map((productGroup, index) => (
                <Box
                  key={`product-${productGroup.product_url}-${index}`}
                  sx={{
                    minWidth: `${100 / visibleCount}%`,
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                    px: 0.25, // Horizontal padding for spacing
                  }}
                >
                  <ProductImageCard
                    productGroup={productGroup}
                    onClick={handleImageClick}
                    onOutfitSuggestion={onOutfitSuggestion}
                    config={config}
                  />
                </Box>
              ))
            ) : (
              filteredImages.map((image, index) => (
                <Box
                  key={`${image.url}-${index}`}
                  sx={{
                    minWidth: `${100 / visibleCount}%`,
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                    px: 0.25, // Horizontal padding for spacing
                  }}
                >
                  <ImageCard
                    image={image}
                    onClick={handleImageClick}
                    onOutfitSuggestion={onOutfitSuggestion}
                    config={config}
                  />
                </Box>
              ))
            )}
          </Box>

          {/* Dots indicator */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 0.5 }}>
              {Array.from({ length: totalPages }).map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  role="button"
                  aria-label={`Go to page ${index + 1}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentIndex(index);
                    }
                  }}
                  sx={{
                    width: index === currentIndex ? 20 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: index === currentIndex ? 'primary.main' : 'grey.300',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      bgcolor: index === currentIndex ? 'primary.dark' : 'grey.400',
                      transform: 'scale(1.2)',
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Fade>
    </>
  );
};

export default ImageCarousel;
